import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "consistencySection",
    "typeTab",
    "tabContent",
    "uploadArea",
    "fileInput",
    "gallery",
    "progressArea",
    "progressBar",
    "progressText",
    "labelInput",
  ]
  static values = {
    uploadUrl: String,
    removeUrl: String,
  }

  toggle(event) {
    const checked = event.target.checked
    if (this.hasConsistencySectionTarget) {
      this.consistencySectionTarget.classList.toggle("hidden", !checked)
    }
  }

  selectTab(event) {
    event.preventDefault()
    const selectedTab = event.currentTarget.dataset.tab

    // Update tab styling
    this.typeTabTargets.forEach((tab) => {
      const isActive = tab.dataset.tab === selectedTab
      tab.classList.toggle("border-indigo-500", isActive)
      tab.classList.toggle("text-indigo-600", isActive)
      tab.classList.toggle("border-transparent", !isActive)
      tab.classList.toggle("text-gray-500", !isActive)
    })

    // Show/hide tab content
    this.tabContentTargets.forEach((content) => {
      content.classList.toggle("hidden", content.dataset.tab !== selectedTab)
    })
  }

  clickUpload(event) {
    // Find the file input within the same upload area
    const uploadArea = event.currentTarget
    const fileInput = uploadArea.querySelector('input[type="file"]')
    if (fileInput) {
      fileInput.click()
    }
  }

  dragOver(event) {
    event.preventDefault()
    event.currentTarget.classList.add("border-indigo-400", "bg-indigo-50")
  }

  dragLeave(event) {
    event.currentTarget.classList.remove("border-indigo-400", "bg-indigo-50")
  }

  handleDrop(event) {
    event.preventDefault()
    event.currentTarget.classList.remove("border-indigo-400", "bg-indigo-50")

    const files = event.dataTransfer.files
    if (files.length > 0) {
      const referenceType = event.currentTarget.dataset.referenceType
      this.uploadFile(files[0], referenceType)
    }
  }

  handleFileSelect(event) {
    const file = event.target.files[0]
    if (file) {
      const referenceType = event.target.dataset.referenceType
      this.uploadFile(file, referenceType)
      event.target.value = "" // Reset for re-upload
    }
  }

  async uploadFile(file, referenceType) {
    const labelInput = this.labelInputTargets.find(
      (i) => i.dataset.tab === referenceType,
    )
    const label = labelInput?.value?.trim() || ""

    const formData = new FormData()
    formData.append("file", file)
    formData.append("reference_type", this.singularType(referenceType))
    if (label) formData.append("label", label)

    // Show progress
    this.showProgress(referenceType)

    try {
      const response = await fetch(this.uploadUrlValue, {
        method: "POST",
        body: formData,
        headers: {
          "X-CSRF-Token": this.csrfToken(),
        },
      })

      const data = await response.json()

      if (data.success) {
        this.appendImageCard(referenceType, data.data)
        this.hideProgress(referenceType)
        if (labelInput) labelInput.value = ""
      } else {
        this.showError(referenceType, data.error || "Upload failed")
      }
    } catch (error) {
      this.showError(referenceType, "Network error during upload")
    }
  }

  async remove(event) {
    event.preventDefault()
    const button = event.currentTarget
    const mediaId = button.dataset.mediaId
    const card = button.closest("[data-reference-card]")

    try {
      const response = await fetch(this.removeUrlValue, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfToken(),
        },
        body: JSON.stringify({ media_id: mediaId }),
      })

      const data = await response.json()
      if (data.success && card) {
        card.remove()
      }
    } catch {
      // Silently fail on remove errors
    }
  }

  // Private helpers

  appendImageCard(referenceType, data) {
    const gallery = this.galleryTargets.find(
      (g) => g.dataset.tab === referenceType,
    )
    if (!gallery) return

    const card = document.createElement("div")
    card.className =
      "relative group rounded-lg overflow-hidden border border-gray-200"
    card.dataset.referenceCard = ""
    card.dataset.mediaId = data.media_id

    card.innerHTML = `
      <img src="/storage/ai_media/${data.image_path.split("/ai_media/").pop()}"
           alt="${data.label || referenceType}"
           class="w-full h-24 object-cover">
      ${data.label ? `<p class="text-xs text-gray-600 px-2 py-1 truncate">${this.escapeHtml(data.label)}</p>` : ""}
      <button type="button"
              data-action="reference-images#remove"
              data-media-id="${data.media_id}"
              class="absolute top-1 right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity"
              title="Remove">
        &times;
      </button>
    `
    gallery.appendChild(card)
  }

  showProgress(referenceType) {
    const area = this.progressAreaTargets.find(
      (a) => a.dataset.tab === referenceType,
    )
    if (area) {
      area.classList.remove("hidden")
      const bar = area.querySelector("[data-reference-images-target='progressBar']")
      const text = area.querySelector("[data-reference-images-target='progressText']")
      if (bar) bar.style.width = "50%"
      if (text) text.textContent = "Uploading..."
    }
  }

  hideProgress(referenceType) {
    const area = this.progressAreaTargets.find(
      (a) => a.dataset.tab === referenceType,
    )
    if (area) {
      const bar = area.querySelector("[data-reference-images-target='progressBar']")
      if (bar) bar.style.width = "100%"
      setTimeout(() => area.classList.add("hidden"), 500)
    }
  }

  showError(referenceType, message) {
    const area = this.progressAreaTargets.find(
      (a) => a.dataset.tab === referenceType,
    )
    if (area) {
      const text = area.querySelector("[data-reference-images-target='progressText']")
      if (text) {
        text.textContent = message
        text.classList.add("text-red-500")
      }
      setTimeout(() => {
        area.classList.add("hidden")
        if (text) text.classList.remove("text-red-500")
      }, 3000)
    }
  }

  singularType(pluralType) {
    const map = {
      characters: "character",
      backgrounds: "background",
      props: "prop",
    }
    return map[pluralType] || pluralType
  }

  csrfToken() {
    const meta = document.querySelector('meta[name="csrf-token"]')
    return meta ? meta.getAttribute("content") : ""
  }

  escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }
}
